import { Box } from "@mui/material";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import DynamicIcon from "@entrilia/shared/components/DynamicIcon";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import type { Category } from "../../../api/types/accessTypes";
import CategoryTag from "../../common/CategoryTag";

const columns: GridColDef<Category>[] = [
  { field: "name", headerName: "Name", flex: 1, minWidth: 220, maxWidth: 400, cellClassName: "highlighted-action" },
  {
    field: "color",
    headerName: "Tag",
    width: 190,
    renderCell: ({ row }) => <CategoryTag externalCategoryId={row.externalId} />,
  },
  {
    field: "effectiveIconName",
    headerName: "Inbox Icon",
    width: 120,
    renderCell: ({ row }) =>
      row.effectiveIconName ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", color: "text.disabled" }}>
          <DynamicIcon iconName={row.effectiveIconName} color="inherit" />
        </Box>
      ) : null,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1.5,
    minWidth: 200,
    renderCell: ({ row }) => (
      <TypographyTooltipEllipsis text={row.description || "—"} typographyProps={{ color: "textSecondary" }} />
    ),
  },
];

interface Props {
  rows: Category[];
  loading: boolean;
  investorPortalEnabled: boolean;
  onEditCategory?: (category: Category) => void;
}

const AccessCategoriesGrid = ({ rows, loading, investorPortalEnabled, onEditCategory }: Props) => {
  const columnVisibilityModel = useMemo(
    () => ({ effectiveIconName: investorPortalEnabled, description: investorPortalEnabled }),
    [investorPortalEnabled]
  );

  const handleRowClick = useCallback(({ row }: GridRowParams<Category>) => onEditCategory?.(row), [onEditCategory]);

  return (
    <DataGrid<Category>
      rows={rows}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      loading={loading}
      disableColumnReorder
      disableColumnSorting
      selectableRows={Boolean(onEditCategory)}
      onRowClick={onEditCategory && handleRowClick}
    />
  );
};

export default AccessCategoriesGrid;
